import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

import { mapRequest } from "../../api/client";
import type { LayerKey, LonLat, OpoContext, OpoDraft } from "../../api/types";
import { Segmented } from "../../components/ui";
import { config } from "../../config";
import { t } from "../../i18n/i18n";
import { handleIndices, movedAxis, terrainAxis, terrainOf } from "../../app/home/opoDraft";
import { LAYER_STYLES } from "../layerStyles";
import { bboxCenter, inIgnCoverage } from "../zones/basemap";
import { verificationBaseStyle, VerificationLayers } from "./VerificationLayers";
import { AXIS_HANDLE_LABEL, type TerrainView, VerificationTerrain } from "./VerificationTerrainLayers";

/** Carte d'aperçu de la vérification d'un OPO (docs/20 §12 et §14) : camp choisi (plan IGN et couches du camp) ou
 *  terrain libre tiré de l'ordre (globe Mapbox ; emprise proposée, terrain en préparation, puis couches du terrain prêt),
 *  fuseau et LIMA, unités du dispositif qui sera créé au temps choisi, lieux résolus, limites et graphiques lus. Usages :
 *  - « view » : regarder ;
 *  - « draw » : déplacer les sommets des limites et les extrémités des LIMA (poignées glissées, au doigt comme à
 *    la souris) ; le fuseau devient celui tracé à la main ;
 *  - « point » : pointer d'un clic un lieu que le répertoire du terrain ne connaît pas ;
 *  - « anchor » : pointer d'un clic le repère des coordonnées écrites sans zone de grille ;
 *  - « axis » : tracer l'axe de l'emprise en deux clics (arrière, puis avant côté ennemi) ;
 *  - « adjust » : déplacer l'arrière, l'avant ou toute l'emprise proposée (poignées glissées, aperçu en direct).
 *  Moteur chargé à la demande, comme la carte du dossier ; cadrage sur l'emprise du camp ou du terrain. Hors de
 *  l'emprise des fonds IGN, le plan IGN n'est pas posé : le globe Mapbox reste seul (comme la vue Manœuvre). */

type Engine = typeof import("../engines/mapbox");
type Vertex = { rail: "left" | "right"; index: number } | { line: "L0" | "L1" | "L2" | "L3"; index: number };
type Axis = { start: LonLat; end: LonLat };
export type VerificationMode = "view" | "draw" | "point" | "anchor" | "axis" | "adjust";

const CAMP_LAYERS: LayerKey[] = ["urban", "forest", "water", "boundary", "rivers", "roads"];

export function VerificationMap({
  draft,
  context,
  phase,
  onPhase,
  mode,
  selectedPlace,
  onPoint,
  onVertex,
  onAnchor,
  onAxis,
  className,
}: {
  draft: OpoDraft;
  context: OpoContext;
  phase: "T1" | "T2" | "T3";
  onPhase: (phase: "T1" | "T2" | "T3") => void;
  mode: VerificationMode;
  selectedPlace: string | null;
  onPoint?: (point: [number, number]) => void;
  onVertex?: (vertex: Vertex, point: [number, number]) => void;
  /** Repère pointé (« anchor »). */
  onAnchor?: (point: [number, number]) => void;
  /** Axe tracé (« axis ») ou ajusté (« adjust »). */
  onAxis?: (axis: Axis) => void;
  /** Taille et marges seulement. */
  className?: string;
}) {
  const [engine, setEngine] = useState<Engine | null>(null);
  const mapRef = useRef<import("react-map-gl/mapbox").MapRef | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Tracé de l'axe en deux clics, et emprise glissée (aperçu en direct, enregistrée au lâcher).
  const [drawStart, setDrawStart] = useState<LonLat | null>(null);
  const [hover, setHover] = useState<LonLat | null>(null);
  const [dragged, setDragged] = useState<Axis | null>(null);
  useEffect(() => {
    const load = config.mapEngine === "mapbox" ? import("../engines/mapbox") : import("../engines/maplibre");
    void load.then((mod) => setEngine(mod as unknown as Engine));
  }, []);
  useEffect(() => {
    if (mode !== "axis") {
      setDrawStart(null);
      setHover(null);
    }
    if (mode !== "adjust") setDragged(null);
  }, [mode]);
  const style = useMemo(() => (config.mapEngine === "mapbox" ? config.mapboxStyle : verificationBaseStyle(config.maplibreGlyphs)), []);
  const terrain = context.map.terrain;
  const zoneMode = context.terrain?.mode === "zone";
  const linked = context.terrain?.zone ?? null;
  const bounds = context.map.bounds;
  const boundsKey = bounds ? bounds.join(",") : "";
  // Cadrage : à chaque camp, au terrain libre lié (et quand il devient prêt), à la première emprise proposée ; pas à
  // chaque ajustement de l'emprise (la carte resterait sous le doigt).
  const fitKey = draft.camp
    ? `camp:${draft.camp}:${boundsKey}`
    : linked
      ? `zone:${linked.id}:${terrain?.ready ? 1 : 0}`
      : bounds
        ? "proposal"
        : "";
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !loaded || !bounds) return;
    // Après la mise en page (fenêtre qui s'ouvre, onglet qui change) : taille du cadre relue, puis cadrage.
    const frame = requestAnimationFrame(() => {
      map.resize();
      map.fitBounds(
        [
          [bounds[0], bounds[1]],
          [bounds[2], bounds[3]],
        ],
        { padding: 24, duration: 0 },
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [fitKey, loaded]);

  if (!engine) return <div className={clsx("rounded-lg border border-line bg-canvas", className)} />;
  const { Map, Source, Layer, Marker, NavigationControl } = engine;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const slug = draft.camp ?? (terrain?.kind === "zone" && terrain.ready ? terrain.slug : null);
  // Plan IGN : seulement dans son emprise (métropole, outre-mer) ; le globe Mapbox ailleurs.
  const ign = config.mapEngine !== "mapbox" || !bounds || inIgnCoverage(bboxCenter(bounds));
  const handles: { key: string; vertex: Vertex; at: [number, number] }[] = [];
  if (mode === "draw") {
    for (const rail of ["left", "right"] as const) {
      const points = draft.corridor[rail];
      for (const index of handleIndices(points.length)) {
        handles.push({ key: `${rail}${index}`, vertex: { rail, index }, at: points[index]! });
      }
    }
    for (const name of ["L0", "L1", "L2", "L3"] as const) {
      const coords = draft.corridor.lines[name] ?? [];
      if (coords.length >= 2) {
        handles.push({ key: `${name}-a`, vertex: { line: name, index: 0 }, at: coords[0]! });
        handles.push({ key: `${name}-b`, vertex: { line: name, index: coords.length - 1 }, at: coords[coords.length - 1]! });
      }
    }
  }
  // Emprise à préparer : tant qu'aucun terrain n'est lié.
  const axis: Axis | null = zoneMode && !linked ? (dragged ?? terrainAxis({ draft, context })) : null;
  const proposal = context.terrain?.proposal;
  const view: TerrainView | null = zoneMode
    ? {
        axis: mode === "axis" ? null : axis,
        level: proposal?.level ?? draft.level,
        levelKey: proposal?.level_key ?? context.level_key,
        footprint: linked && !terrain?.ready ? (terrain?.footprint?.coordinates[0] ?? null) : null,
        anchor: terrainOf(draft).anchor,
        drawing: mode === "axis" ? { start: drawStart, hover } : null,
        tag: mode === "adjust",
      }
    : null;
  const axisHandles: { key: "start" | "end" | "middle"; at: LonLat }[] =
    mode === "adjust" && axis
      ? [
          { key: "start", at: axis.start },
          { key: "middle", at: [(axis.start[0] + axis.end[0]) / 2, (axis.start[1] + axis.end[1]) / 2] },
          { key: "end", at: axis.end },
        ]
      : [];
  const clicking = mode === "point" || mode === "anchor" || mode === "axis";

  return (
    <div className={clsx("relative overflow-hidden rounded-lg border border-line bg-canvas", clicking && "cursor-crosshair", className)}>
      <Map
        ref={(ref: unknown) => {
          mapRef.current = ref as typeof mapRef.current;
        }}
        attributionControl={false}
        mapboxAccessToken={config.mapboxToken || undefined}
        transformRequest={mapRequest as never}
        initialViewState={{ longitude: 2.6, latitude: 46.6, zoom: 5.2 }}
        mapStyle={style as never}
        style={{ position: "absolute", inset: 0 }}
        cursor={clicking ? "crosshair" : undefined}
        onLoad={() => setLoaded(true)}
        onMouseMove={(e: { lngLat: { lng: number; lat: number } }) => {
          if (mode === "axis" && drawStart) setHover([e.lngLat.lng, e.lngLat.lat]);
        }}
        onClick={(e: { lngLat: { lng: number; lat: number } }) => {
          const at: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          if (mode === "point") onPoint?.(at);
          else if (mode === "anchor") onAnchor?.(at);
          else if (mode === "axis") {
            if (!drawStart) setDrawStart(at);
            else {
              onAxis?.({ start: drawStart, end: at });
              setDrawStart(null);
              setHover(null);
            }
          }
        }}
      >
        {ign && (
          <Source id="verif-ign-plan" type="raster" tiles={[config.ignPlanUrl]} tileSize={256} maxzoom={18} attribution="© IGN Géoplateforme">
            <Layer
              id="verif-ign-plan"
              type="raster"
              paint={{ "raster-saturation": -0.6, "raster-brightness-min": 0.05, "raster-contrast": -0.08, "raster-opacity": 0.85 }}
            />
          </Source>
        )}
        {slug &&
          CAMP_LAYERS.map((key) => (
            <Source key={`${slug}-${key}`} id={`verif-${key}`} type="geojson" data={`${origin}/api/maps/${slug}/layers/${key}`}>
              {LAYER_STYLES[key].map((s) => (
                <Layer
                  key={s.id}
                  id={`verif-${s.id}`}
                  type={s.type as never}
                  // Moteur de carte : une propriété `undefined` est refusée (« object expected ») ; posée si elle existe.
                  {...(s.minzoom !== undefined ? { minzoom: s.minzoom } : {})}
                  {...(s.paint ? { paint: s.paint as never } : {})}
                  {...(s.layout ? { layout: s.layout as never } : {})}
                />
              ))}
            </Source>
          ))}
        {view && <VerificationTerrain Source={Source} Layer={Layer} Marker={Marker} view={view} />}
        <VerificationLayers
          Source={Source}
          Layer={Layer}
          draft={draft}
          context={context}
          phase={phase}
          selectedPlace={selectedPlace}
          showPlaces={mode !== "draw"}
        />
        {handles.map((h) => (
          <Marker
            key={h.key}
            longitude={h.at[0]}
            latitude={h.at[1]}
            draggable
            onDragEnd={(e: { lngLat: { lng: number; lat: number } }) => onVertex?.(h.vertex, [e.lngLat.lng, e.lngLat.lat])}
          >
            {/* Zone de prise de 40 px (au doigt), poignée visible de 16 px au centre. */}
            <span
              role="button"
              aria-label={t("Déplacer ce sommet")}
              title={t("Glissez pour déplacer")}
              className="flex size-10 cursor-grab items-center justify-center active:cursor-grabbing"
            >
              <span className="block size-4 rounded-full border-2 border-accent bg-surface shadow-sm" />
            </span>
          </Marker>
        ))}
        {axis &&
          axisHandles.map((h) => (
            <Marker
              key={h.key}
              longitude={h.at[0]}
              latitude={h.at[1]}
              draggable
              onDrag={(e: { lngLat: { lng: number; lat: number } }) => setDragged(movedAxis(axis, h.key, [e.lngLat.lng, e.lngLat.lat]))}
              onDragEnd={(e: { lngLat: { lng: number; lat: number } }) => {
                // Axe d'avant le glissé (`terrainAxis`), déplacé jusqu'au point de lâcher ; le brouillon le porte aussitôt.
                const base = terrainAxis({ draft, context }) ?? axis;
                onAxis?.(movedAxis(base, h.key, [e.lngLat.lng, e.lngLat.lat]));
                setDragged(null);
              }}
            >
              <span
                role="button"
                aria-label={AXIS_HANDLE_LABEL[h.key]()}
                title={AXIS_HANDLE_LABEL[h.key]()}
                className="flex size-10 cursor-grab items-center justify-center active:cursor-grabbing"
              >
                <span className={clsx("block rounded-full border-2 border-accent bg-surface shadow-sm", h.key === "middle" ? "size-5" : "size-4")} />
              </span>
            </Marker>
          ))}
        <NavigationControl position="bottom-right" showCompass={false} />
      </Map>
      <div className="absolute top-2 left-2">
        <Segmented
          ariaLabel={t("Temps affiché")}
          size="sm"
          fill={false}
          options={[
            { value: "T1", label: "T1" },
            { value: "T2", label: "T2" },
            { value: "T3", label: "T3" },
          ]}
          value={phase}
          onChange={onPhase}
          className="rounded-lg bg-surface/90 shadow-sm"
        />
      </div>
    </div>
  );
}
